import React, { useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { EmberField } from '@/components/shared/EmberField'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'
import { generateSanctumItinerary } from '@/services/ai'
import { getDestinationImages, fetchImages, type Img } from '@/services/images'
import { Attribution } from '@/components/shared/Attribution'
import { openDeal, NETWORK_LABELS } from '@/services/affiliates'
import type { Destination, Stop } from '@/types'

const DAY_OPTIONS = [2, 3, 4, 5, 6]

interface GeneratedDay { day: number; title: string; highlight: string }

// Curated fallback summary built straight from the destination description —
// used the instant the screen mounts (before/if AI resolves) so the screen
// is NEVER blank.
function curatedSummary(dest: Destination, days: number): string {
  const base = dest.description
  return `${base} Over ${days} unhurried days, this pilgrimage moves at the pace the sacred deserves — quiet mornings, meaningful stops, and space to sit with what you find.`
}

// Reshape the destination's curated stops onto N days, cycling stops
// round-robin if N exceeds the number of curated stops so every day still
// has a real, named place attached to it.
function stopsForDay(dest: Destination, dayIndex: number, days: number): Stop {
  const stops = dest.stops.length > 0 ? dest.stops : [{ time: `Day ${dayIndex + 1}`, name: dest.name, description: dest.description, spiritualNote: '◈ A day held in stillness' }]
  return stops[dayIndex % stops.length]
}

function curatedDayPlan(dest: Destination, days: number): GeneratedDay[] {
  return Array.from({ length: days }, (_, i) => {
    const stop = stopsForDay(dest, i, days)
    return {
      day: i + 1,
      title: stop.name.split(' — ')[0].split(',')[0],
      highlight: stop.description,
    }
  })
}

// Day photo — same graceful gradient-fallback pattern used across the app.
function DayPhoto({ dest, query, size = 90 }: { dest: Destination; query: string; size?: number }) {
  const [img, setImg] = useState<Img | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setImg(null); setFailed(false)
    let cancelled = false
    fetchImages(query, 1).then((imgs) => { if (!cancelled && imgs[0]) setImg(imgs[0]) })
    return () => { cancelled = true }
  }, [query])

  const showPhoto = img && !failed

  return (
    <div style={{ width: size, height: size, flexShrink: 0, position: 'relative', overflow: 'hidden', background: dest.gradient, border: '1px solid var(--hairline)' }}>
      {showPhoto && (
        <img
          src={img!.url}
          alt={img!.alt || dest.name}
          onError={() => setFailed(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'saturate(0.78) brightness(0.72)' }}
        />
      )}
      {!showPhoto && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.34 }}>
          {dest.emoji}
        </div>
      )}
    </div>
  )
}

export function FullItineraryScreen() {
  const { selectedDestination, dnaKey, setScreen } = useAppStore()
  const profile = DNA_PROFILES[dnaKey]
  const dest = selectedDestination ?? (DESTINATIONS[dnaKey]?.[0] || null)

  const defaultDays = dest?.stops?.length ? Math.min(6, Math.max(2, dest.stops.length)) : 3
  const [days, setDays] = useState(defaultDays)
  const [galleryImgs, setGalleryImgs] = useState<Img[]>([])
  const [aiDays, setAiDays] = useState<GeneratedDay[] | null>(null)
  const [aiSummary, setAiSummary] = useState<string>('')
  const [aiLoading, setAiLoading] = useState(false)
  const [usedAI, setUsedAI] = useState(false)

  useEffect(() => {
    if (!dest) return
    let cancelled = false
    getDestinationImages(dest, 6).then((imgs) => { if (!cancelled) setGalleryImgs(imgs) })
    return () => { cancelled = true }
  }, [dest?.id])

  useEffect(() => {
    if (!dest || !profile) return
    let cancelled = false
    setAiLoading(true)
    generateSanctumItinerary(dest.name, profile.name, days)
      .then((plan) => {
        if (cancelled) return
        // Detect whether this is real AI output vs our silent curated fallback
        // (fallback always produces exactly "Day N" titles + the fixed sentence).
        const looksCurated = plan.every((p, i) => p.title === `Day ${i + 1}` && p.highlight === `A day of quiet pilgrimage in ${dest.name}.`)
        setAiDays(plan)
        setUsedAI(!looksCurated)
        setAiLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setAiDays(curatedDayPlan(dest, days))
        setUsedAI(false)
        setAiLoading(false)
      })
    return () => { cancelled = true }
  }, [dest?.id, days])

  if (!dest || !profile) { setScreen('journey'); return null }

  const dayPlan = aiDays && aiDays.length > 0 ? aiDays : curatedDayPlan(dest, days)
  const summaryText = usedAI && aiSummary ? aiSummary : curatedSummary(dest, days)

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', position: 'relative' }}>
      <EmberField count={10} style={{ position: 'fixed' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <Topbar center="Full Pilgrimage" backTo="card" backLabel="← Journey Card" />

        <div style={{ width: '100%', maxWidth: 760, margin: '0 auto', padding: '28px 20px 60px' }}>

          {/* Country › Destination hierarchy label */}
          {dest.country && (
            <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.14em', color: 'var(--candle2)', textTransform: 'uppercase', marginBottom: 8 }}>
              {dest.flag} {dest.country} <span style={{ color: 'rgba(255,255,255,0.4)', margin: '0 4px' }}>›</span> <span style={{ color: 'var(--ivory-dim)' }}>{dest.name}</span>
            </div>
          )}

          {/* Header */}
          <motion.h1 initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            style={{ fontFamily: 'var(--serif-caps)', fontSize: 'clamp(24px, 4vw, 34px)', color: 'var(--ivory)', lineHeight: 1.2, marginBottom: 14, letterSpacing: '0.03em' }}>
            Your perfect {days} days in {dest.name}
          </motion.h1>

          {/* Narrative intro */}
          {aiLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 40, marginBottom: 14 }}>
              {[0, 1, 2].map((i) => (
                <motion.div key={i} style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--candle2)' }}
                  animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.28 }} />
              ))}
              <span style={{ fontFamily: 'var(--serif-body)', fontSize: 13, color: 'var(--ivory-faint)', fontStyle: 'italic' }}>
                Sanctum is composing your pilgrimage…
              </span>
            </div>
          ) : (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
              style={{ fontFamily: 'var(--serif-body)', fontSize: 15, color: 'var(--ivory-dim)', lineHeight: 1.85, maxWidth: 640, marginBottom: 14, fontStyle: 'italic' }}>
              {summaryText}
            </motion.p>
          )}

          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(201,151,58,0.08)', border: '1px solid var(--hairline2)', padding: '3px 10px', marginBottom: 20 }}>
            <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.12em', color: 'var(--candle2)', textTransform: 'uppercase' }}>
              {usedAI ? '◈ Personalised by Claude AI' : '◈ CACHED · Curated by Sanctum'}
            </span>
          </div>

          {/* Day-count selector */}
          <div style={{ marginBottom: 26 }}>
            <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.14em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 10 }}>
              ◈ Shape Your Pilgrimage
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {DAY_OPTIONS.map((n) => (
                <button
                  key={n}
                  onClick={() => setDays(n)}
                  style={{
                    padding: '9px 18px',
                    fontFamily: 'var(--serif-caps)', fontSize: 12, letterSpacing: '0.08em',
                    border: days === n ? '2px solid var(--candle)' : '1px solid var(--hairline)',
                    background: days === n ? 'rgba(201,151,58,0.12)' : 'var(--forest2)',
                    color: days === n ? 'var(--candle2)' : 'var(--ivory-dim)',
                    cursor: 'pointer',
                    boxShadow: days === n ? '0 0 16px rgba(201,151,58,0.3)' : 'none',
                    transition: 'all 0.25s ease',
                  }}
                >
                  {n} Days
                </button>
              ))}
            </div>
          </div>

          {/* Day-by-day list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 32 }}>
            <AnimatePresence mode="wait">
              <motion.div key={days} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {dayPlan.map((d, i) => {
                  const stop = stopsForDay(dest, i, days)
                  const galleryImg = galleryImgs[i % Math.max(galleryImgs.length, 1)]
                  return (
                    <motion.div key={d.day} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                      style={{ border: '1px solid var(--hairline)', background: 'var(--forest2)', overflow: 'hidden', boxShadow: '0 6px 24px rgba(0,0,0,0.35)' }}>
                      <div style={{ display: 'flex', gap: 0 }}>
                        <DayPhoto dest={dest} query={`${stop.name} ${dest.name}`} size={100} />
                        <div style={{ flex: 1, padding: '12px 14px', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                            <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'var(--candle2)', textTransform: 'uppercase' }}>
                              DAY {d.day}
                            </span>
                            <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 15, color: 'var(--ivory)', letterSpacing: '0.02em' }}>
                              {d.title}
                            </span>
                          </div>
                          <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--ivory-dim)', lineHeight: 1.6, fontStyle: 'italic', marginBottom: 4 }}>
                            {d.highlight}
                          </div>
                          {galleryImg && (
                            <Attribution image={galleryImg} style={{ display: 'inline-block' }} />
                          )}
                        </div>
                      </div>

                      {/* Stop detail row */}
                      <div style={{ borderTop: '1px solid var(--hairline)', padding: '10px 14px' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 }}>
                          <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'var(--ivory-faint)', textTransform: 'uppercase' }}>
                            {stop.time}
                          </span>
                          <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 12, color: 'var(--ivory)' }}>{stop.name}</span>
                        </div>
                        <div style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--ivory-dim)', lineHeight: 1.65, fontStyle: 'italic', marginBottom: 4 }}>
                          {stop.description}
                        </div>
                        <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, color: 'var(--candle2)', letterSpacing: '0.05em' }}>
                          {stop.spiritualNote}
                        </div>

                        {/* Inline partner offer woven per day (round-robin through deals) */}
                        {dest.deals.length > 0 && (
                          <button
                            onClick={() => openDeal(dest.deals[i % dest.deals.length], dest.name)}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 4px', marginTop: 10, background: 'rgba(201,151,58,0.06)', border: '1px solid var(--hairline2)', cursor: 'pointer', textAlign: 'left' }}
                          >
                            <span style={{ fontSize: 15 }}>{dest.deals[i % dest.deals.length].icon}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 10, color: 'var(--ivory)', letterSpacing: '0.03em' }}>
                                {dest.deals[i % dest.deals.length].title}
                              </div>
                              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--candle2)', letterSpacing: '0.06em', marginTop: 2, textTransform: 'uppercase' }}>
                                via {NETWORK_LABELS[dest.deals[i % dest.deals.length].network] || dest.deals[i % dest.deals.length].network}
                              </div>
                            </div>
                            <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid var(--hairline2)', color: 'var(--candle2)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                              {dest.deals[i % dest.deals.length].saving}
                            </div>
                          </button>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Partner offers block */}
          {dest.deals.length > 0 && (
            <div style={{ border: '1px solid var(--hairline)', background: 'var(--forest2)', padding: '16px 16px 4px', marginBottom: 28 }}>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'var(--ivory-faint)', textTransform: 'uppercase', marginBottom: 8 }}>
                ◈ Partner Offers For This Pilgrimage
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {dest.deals.map((deal, i) => (
                  <motion.button key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                    onClick={() => openDeal(deal, dest.name)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--hairline)', cursor: 'pointer', textAlign: 'left' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(201,151,58,0.06)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div style={{ width: 34, height: 34, background: 'var(--forest3)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{deal.icon}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 11, color: 'var(--ivory)', letterSpacing: '0.03em' }}>{deal.title}</div>
                      <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'var(--ivory-faint)', marginTop: 1, fontStyle: 'italic' }}>{deal.detail}</div>
                      <div style={{ display: 'inline-block', fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--candle2)', background: 'rgba(201,151,58,0.08)', border: '1px solid var(--hairline2)', padding: '1px 6px', letterSpacing: '0.06em', marginTop: 3, textTransform: 'uppercase' }}>via {NETWORK_LABELS[deal.network] || deal.network}</div>
                    </div>
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid var(--hairline2)', color: 'var(--candle2)', whiteSpace: 'nowrap', flexShrink: 0 }}>{deal.saving}</div>
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Bottom CTA */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => setScreen('card')} style={{ flex: 1, minWidth: 200, padding: 14, background: 'var(--candle)', color: 'var(--forest)', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', border: 'none', cursor: 'pointer', boxShadow: '0 0 20px rgba(201,151,58,0.35)' }}>
              ← Back to Journey Card
            </button>
            <button onClick={() => setScreen('card')} style={{ flex: 1, minWidth: 200, padding: 14, background: 'var(--forest2)', border: '1px solid var(--hairline)', color: 'var(--ivory-dim)', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer', textTransform: 'uppercase' }}>
              ◈ Send to Phone
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
